import { gql } from '@apollo/client';
import { get, omit, isNil, sortBy } from 'lodash-es';
import { EdmundsAPI } from 'client/data/api/api-client';
import { ClientConfig } from 'client/configuration';
import { NO_VIN } from 'client/data/models/inventory';
import { getMetricsHeaders, getMetricsPageHeaders } from 'client/utils/metrics-header';
import { recordError } from 'site-modules/shared/utils/car-buying/client-error-handler';
import { mapLoanOffersToRetailModel } from 'site-modules/shared/utils/car-buying/map-to-retail-model';
import { getVehicleAge } from 'site-modules/shared/utils/inventory-utils/get-vehicle-age';
import {
  DEFAULT_LEASE_ANNUAL_MILEAGE,
  DEFAULT_STYLE_ID,
  CREDIT_SCORE_GOOD,
  CREDIT_SCORE_EXCELLENT,
  CREDIT_SCORE_POOR,
  CREDIT_SCORE_FAIR,
} from 'site-modules/shared/constants/calculator/calculator';
import { CREDIT_MAX_SCORE } from 'site-modules/shared/constants/car-buying';
import { getDefaultApiHeaders } from 'site-modules/shared/utils/car-buying/api-helper';
import { isMockVin } from 'site-modules/shared/utils/car-buying/mock-util';
import { LEASE_TERMS_INCENTIVES } from 'stubs/car-buying/api-stub';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { getCreditScoreName } from 'site-modules/shared/utils/calculator/calculator-helper';
import dateFormat from 'dateformat';

const TIER_CREDIT_SCORE_MAP = {
  [CREDIT_SCORE_EXCELLENT]: 'TIER1',
  [CREDIT_SCORE_GOOD]: 'TIER2',
  [CREDIT_SCORE_FAIR]: 'TIER3',
  [CREDIT_SCORE_POOR]: 'TIER4',
};

/**
 * Gets allowed credit score.
 * @param {number} creditScore
 * @returns {number}
 */
export const getAllowedCreditScore = creditScore => (creditScore === CREDIT_MAX_SCORE ? creditScore - 1 : creditScore);

async function fetchLeaseOffers(
  { incentives, year, mileage, ...leaseOfferParams },
  { visitorId, pageName, venomVersion }
) {
  const leaseTermsApi = `/car-buying/v1/lease-terms`;

  const bodyParams = {
    ...leaseOfferParams,
    incentives: incentives?.map(inc => omit(inc, 'id')),
  };

  const options = {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...getDefaultApiHeaders(visitorId),
      ...getMetricsHeaders('getLeaseOffers', pageName, venomVersion),
    },
    body: JSON.stringify(bodyParams),
    timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
    showAPIError: true,
  };

  try {
    return await EdmundsAPI.fetchJson(leaseTermsApi, options);
  } catch (error) {
    recordError();
    throw new Error(error);
  }
}

export const adjustOffersWithIncentives = ({ offers, incentives }) =>
  Object.entries(offers.annualMileage).reduce(
    (acc, [mileageKey, mileageOffers]) => {
      acc.annualMileage[mileageKey] = mileageOffers.map(offer => ({ ...offer, incentives }));

      return acc;
    },
    { annualMileage: {} }
  );

function replaceMockedVinIncentives({ incentives, vin, leaseOffers }) {
  if (!isMockVin(vin)) {
    return leaseOffers;
  }

  const selectedIncentives = (incentives || []).map(({ incentiveId }) => LEASE_TERMS_INCENTIVES[incentiveId]);

  return adjustOffersWithIncentives({ incentives: selectedIncentives, offers: leaseOffers });
}

export async function getLeaseOffersWithApiIncentives({ filteredIncentives, ...leaseOfferParams }, ...rest) {
  let incentives = filteredIncentives;
  if (Array.isArray(filteredIncentives)) {
    const incentivesIds = Array.from(new Set(filteredIncentives.map(incentive => incentive.incentiveId)));
    incentives = incentivesIds.map(id => filteredIncentives.find(incentive => incentive.incentiveId === id));
  }

  const leaseOffers = await fetchLeaseOffers({ incentives, ...leaseOfferParams }, ...rest);

  return replaceMockedVinIncentives({ vin: leaseOfferParams.vin, leaseOffers, incentives: filteredIncentives });
}

export async function getLeaseOffersWithoutIncentives({ filteredIncentives, ...leaseOfferParams }, ...rest) {
  const offers = await fetchLeaseOffers({ incentives: [], ...leaseOfferParams }, ...rest);

  return adjustOffersWithIncentives({ offers, incentives: [] });
}

export const APR_SUBTYPE_IDS = [153, 3]; // 'Alternative APR', 'Special APR'

function checkIncentivesCompatible(incentives, compatibleBy) {
  return isNil(incentives) || incentives.length === 0 || incentives.every(incentive => compatibleBy(incentive));
}

function areAllIncentivesCompatibleBySubProgram({ incentives, compatibleIncentiveIds }) {
  return checkIncentivesCompatible(incentives, incentive =>
    compatibleIncentiveIds.includes(incentive.subprogramId?.toString())
  );
}

function areAllIncentivesCompatibleById({ incentives, compatibleIncentiveIds }) {
  return checkIncentivesCompatible(incentives, incentive => compatibleIncentiveIds.includes(incentive.id?.toString()));
}

export function getBackupApr(aprOffers) {
  return {
    aprs: aprOffers.map(dataItem => ({
      apr: dataItem.apr || 0,
      termMonths: dataItem.termMonths,
    })),
  };
}

export async function getIncentivesGQL({ zipCode, vin, styleId, creditScore, headers, inventoryCodes }) {
  const incentiveFilters = {
    zip: zipCode,
    types: { in: ['CUSTOMER_APR'] },
    subtypeIds: { in: APR_SUBTYPE_IDS },
    styleIds: [styleId],
    creditScore,
    inventoryCodes,
  };
  const creditScoreName = getCreditScoreName(creditScore);
  const data = await EdmundsGraphQLFederation.query(
    gql`
      query (
        $incentiveFilters: IncentiveFilters!
        $styleId: String!
        $vin: String!
        $creditTier: CreditTier!
        $isVinSearch: Boolean!
        $noStyleId: Boolean!
      ) {
        style(styleId: $styleId) @skip(if: $isVinSearch) {
          id
          makeName
          financeAPR {
            backupAPRs(creditRatingTier: $creditTier) {
              apr
              creditRatingTier
              tierMin
              tierMax
              termMonths
            }
          }
        }
        inventories(filters: { vins: [$vin] }) @include(if: $isVinSearch) {
          inventories {
            financeAPR {
              backupAPRs(creditRatingTier: $creditTier) {
                apr
                creditRatingTier
                tierMin
                tierMax
                termMonths
              }
            }
          }
        }
        incentives(incentiveFilters: $incentiveFilters) @skip(if: $noStyleId) {
          incentives {
            id
            subtype
            startDate
            endDate
            compatibleIncentiveIds
            aprs {
              apr
              termMonths
            }
          }
        }
      }
    `,
    {
      incentiveFilters,
      styleId: styleId || '',
      vin: vin || '',
      creditTier: TIER_CREDIT_SCORE_MAP[creditScoreName],
      isVinSearch: !!vin,
      noStyleId: !styleId,
    },
    { headers }
  );

  const purchaseOffers = sortBy(get(data, 'incentives.incentives', []), ['subtype']);
  const backupAPRs = getBackupApr(
    get(data, `${vin ? 'inventories.inventories[0]' : 'style'}.financeAPR.backupAPRs`, [])
  );
  return [backupAPRs, ...purchaseOffers];
}

async function getAprInventoryIncentives({ vin, styleId, creditScore, headers, filteredIncentives }) {
  const vinIncentiveFilters = {
    activeDate: dateFormat(new Date(), 'yyyy-mm-dd'),
    creditScore,
    transactionTypes: { in: ['FINANCE'] },
    types: { in: ['CUSTOMER_APR', 'CUSTOMER_CASH_AND_APR'] },
  };
  const creditScoreName = getCreditScoreName(creditScore);
  const data = await EdmundsGraphQLFederation.query(
    gql`
      query (
        $vinIncentiveFilters: VinIncentiveFilters!
        $styleId: String!
        $vin: String!
        $creditTier: CreditTier!
        $isVinSearch: Boolean!
        $page: PageRequest!
      ) {
        style(styleId: $styleId) @skip(if: $isVinSearch) {
          id
          makeName
          financeAPR {
            backupAPRs(creditRatingTier: $creditTier) {
              apr
              creditRatingTier
              tierMin
              tierMax
              termMonths
            }
          }
        }
        inventories(filters: { vins: [$vin] }) @include(if: $isVinSearch) {
          inventories {
            financeAPR {
              backupAPRs(creditRatingTier: $creditTier) {
                apr
                creditRatingTier
                tierMin
                tierMax
                termMonths
              }
            }
            incentiveList(page: $page, vinIncentiveFilters: $vinIncentiveFilters) {
              incentives {
                id
                subtype
                startDate
                endDate
                compatibleIncentiveIds
                aprs {
                  apr
                  termMonths
                }
                inventoryCode
              }
            }
          }
        }
      }
    `,
    {
      vinIncentiveFilters,
      styleId: styleId || '',
      vin: vin || '',
      creditTier: TIER_CREDIT_SCORE_MAP[creditScoreName],
      isVinSearch: !!vin,
      page: { pageNum: 1, pageSize: 16 },
    },
    { headers }
  );

  const purchaseOffers = sortBy(get(data, 'inventories.inventories[0].incentiveList.incentives', []), ['subtype']);
  const backupAPRs = getBackupApr(
    get(data, `${vin ? 'inventories.inventories[0]' : 'style'}.financeAPR.backupAPRs`, [])
  );

  return [
    backupAPRs,
    ...purchaseOffers.filter(offer =>
      areAllIncentivesCompatibleById({
        incentives: filteredIncentives,
        compatibleIncentiveIds: offer.compatibleIncentiveIds,
      })
    ),
  ];
}

export function getOfferOrFallback({ selectedMileage, selectedTerm, offer }) {
  const defaultOffer = {
    annualMileage: selectedMileage,
    apr: 0,
    term: selectedTerm,
    errorMsg: 'Please contact dealer for current finance programs and payment estimates.',
  };

  return offer || defaultOffer;
}

export async function fetchLoanOffersTermsApi(
  { styleId, zipCode, creditScore, transId, year, mileage = 0, filteredIncentives },
  { visitorId, pageName, venomVersion, isUsed }
) {
  const scoreParam = transId ? `transId=${transId}` : `creditScore=${getAllowedCreditScore(creditScore)}`;
  const url = `/car-buying/v1/loan-terms?styleid=${styleId || DEFAULT_STYLE_ID}&zip=${zipCode}&${scoreParam}${
    isUsed ? `&vehicleAge=${getVehicleAge(year)}&mileage=${mileage}&type=used` : ''
  }`;
  const headers = {
    'content-type': 'application/json',
    ...getDefaultApiHeaders(visitorId),
    ...getMetricsHeaders('getLoanOffers', pageName, venomVersion),
  };
  try {
    let loanOffers = await EdmundsAPI.fetchJson(url, { headers });
    loanOffers = {
      annualMileage: {
        [DEFAULT_LEASE_ANNUAL_MILEAGE]: mapLoanOffersToRetailModel(loanOffers, filteredIncentives),
      },
    };
    return loanOffers;
  } catch (error) {
    recordError();
    throw new Error(error);
  }
}

export async function fetchLoanInventoryOffers(
  { vin, styleId, zipCode, creditScore, filteredIncentives },
  { pageName, venomVersion, pageCategory, xTraceId, protocol, hostName, url: headerUrl }
) {
  try {
    return await getAprInventoryIncentives({
      zipCode,
      styleId,
      vin,
      creditScore: getAllowedCreditScore(creditScore),
      headers: getMetricsPageHeaders({
        actionName: 'getLoanOffers',
        pageName,
        venomVersion,
        pageCategory,
        xTraceId,
        protocol,
        hostName,
        url: headerUrl,
      }),
      filteredIncentives,
    });
  } catch (error) {
    recordError();
    throw new Error(error);
  }
}

/**
 * Gets loan offers.
 * @param {Object} vehicleData
 * @param {string} vehicleData.styleId
 * @param {string} vehicleData.zipCode
 * @param {number} vehicleData.creditScore
 * @param {string} vehicleData.transId
 * @param {number|string} vehicleData.year
 * @param {number} vehicleData.mileage
 * @param {Object} pageParams
 * @param {string} pageParams.visitorId
 * @param {string} pageParams.pageName
 * @param {string} pageParams.venomVersion
 * @param {string} pageParams.pageCategory
 * @param {string} pageParams.xTraceId
 * @param {string} pageParams.protocol
 * @param {string} pageParams.hostName
 * @param {boolean} pageParams.url
 * @param {boolean} pageParams.isUsed
 * @param {Function} fetcher
 * @returns {Promise<*>}
 */
async function getLoanOffers(vehicleData, pageParams, fetcher = fetchLoanOffersTermsApi) {
  return fetcher(vehicleData, pageParams);
}

/**
 * Gets lease/loan offers based on isPurchase flag.
 * @param {object} data
 * @param {boolean }isPurchase
 * @param {object} pageParams
 * @param {Function} loanOffersFetcher
 * @param {Function} leaseOffersFetcher
 * @returns {Promise<*>}
 */
export async function getOffers(
  data,
  {
    isPurchase,
    loanOffersFetcher = fetchLoanOffersTermsApi,
    leaseOffersFetcher = getLeaseOffersWithApiIncentives,
    ...pageParams
  }
) {
  const DATA_VALUES_TO_OMIT = ['selectedTerm'];
  return isPurchase
    ? getLoanOffers(data, pageParams, loanOffersFetcher)
    : leaseOffersFetcher(omit(data, DATA_VALUES_TO_OMIT), pageParams);
}

export function getLoanPaymentUrl({
  customVehiclePrice,
  price,
  primaryIncentiveAmountValue,
  params,
  additionalParams = '',
}) {
  const apiUrl = '/calculators/v2/loanpayment/?';
  const aggregationApiUrl = '/car-buying/v1/aggregation/calculate/loanpayment?';

  // If user inputs custom vehicle price then monthly payments for calculator and for price summary widget should be different
  if (customVehiclePrice) {
    if (primaryIncentiveAmountValue) {
      /**
       * If there are primary incentives, which apply by default, then we need to make 2 API calls to calculate 2 monthly payments
       *  1 - api call to get monthly payment for calcularor, based on customVehiclePrice
       *  2 - api call to get monthly payment for price summary widget, based on ESP
       */
      return [
        `${apiUrl}salesPrice=${customVehiclePrice}&primaryIncentiveAmount=0${params}${additionalParams}`,
        `${apiUrl}salesPrice=${price}&primaryIncentiveAmount=${primaryIncentiveAmountValue}${params}${additionalParams}`,
      ];
    }
    // If there are no primary incentives, then we can make one car-buying/v1/aggregation/calculate/loanpayment API call to calculate 2 monthly payments
    return `${aggregationApiUrl}salesPrices=${customVehiclePrice},${price}&primaryIncentiveAmount=${primaryIncentiveAmountValue}${params}${additionalParams}`;
  }
  // If user doesn't input custom vehicle price then monthly payments for calculator and for price summary widget should be the same
  return `${apiUrl}salesPrice=${price}&primaryIncentiveAmount=${primaryIncentiveAmountValue}${params}${additionalParams}`;
}

const fetchUsedAprs = async (
  { styleId, taxesAndFeesZipCode, year, creditScore, mileage },
  { pageName, venomVersion }
) => {
  const data = await EdmundsAPI.fetchJson(
    `/usedaprs/v1/?styleId=${styleId || DEFAULT_STYLE_ID}&zipCode=${taxesAndFeesZipCode}&vehicleAge=${getVehicleAge(
      year
    )}&creditScore=${creditScore}&vehicleMileage=${mileage || 0}`,
    {
      headers: getMetricsHeaders('loanAPRsUsed', pageName, venomVersion),
    }
  );

  return data && data.terms;
};

const prepareAprsIncentivesFetchParams = (
  { taxesAndFeesZipCode, styleId, vin, creditScore, numberOfMonths, filteredIncentives },
  { pageName, venomVersion, pageCategory, xTraceId, protocol, hostName, url }
) => ({
  zipCode: taxesAndFeesZipCode,
  styleId,
  vin: vin === NO_VIN ? '' : vin,
  creditScore,
  selectedTerm: numberOfMonths,
  headers: getMetricsPageHeaders({
    actionName: 'getLoanOffers',
    pageName,
    venomVersion,
    pageCategory,
    xTraceId,
    protocol,
    hostName,
    url,
  }),
  filteredIncentives,
  inventoryCodes: ['NEW'], // Add isNewType check if function is used for used vehicles
});

export const fetchAprs = async (data, pageInfo) => {
  const { isNewType, filteredIncentives } = data;
  if (isNewType) {
    const [backupAprs, ...purchaseOffers] = await getIncentivesGQL(prepareAprsIncentivesFetchParams(data, pageInfo));
    return [
      backupAprs,
      ...purchaseOffers.filter(offer =>
        areAllIncentivesCompatibleBySubProgram({
          incentives: filteredIncentives,
          compatibleIncentiveIds: offer.compatibleIncentiveIds,
        })
      ),
    ];
  }

  return fetchUsedAprs(data, pageInfo);
};

export const fetchAprsInventoryIncentives = async (data, pageInfo) => {
  const { isNewType } = data;
  if (isNewType) {
    return getAprInventoryIncentives(prepareAprsIncentivesFetchParams(data, pageInfo));
  }

  return fetchUsedAprs(data, pageInfo);
};
